import * as React from "react";
import "@progress/kendo-react-intl";
import "@progress/kendo-react-dropdowns";
import "react-router-dom";
import { useState, useEffect } from "react";
import { useCompany } from "services/companyService";
import * as api from "../../api/index";
import {
    clearMattersBalances,
    getMattersBalances,
} from "actions/matters.action";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch } from "react-redux";
import MatterAccount from "../MatterLedger/MatterAccount";
import { AmountFormat, AmountFormatCell, DateFormatCell } from "components/common/formatter";
import {
    PayCell,
    createBatchPostingCommandCell,
    createBatchPostingDocumentCounterCell,
} from "components/BatchPosting/GridComponents";
import { fetchBatchPostingDocuments } from "components/BatchPosting/batchPostingDocuments";
import PostingAuthDialog from "components/BatchPosting/PostingAuthDialog";
import { PostingSubmitFilter } from "components/BatchPosting/SubmitFunction";
import Form from "components/BatchPosting/Form";
import MatterDetails from "pages/MatterLedger/MatterDetails";
import { TransactionPeriod } from "components/common/TransactionPeriod";
import { getSelectedSessionPeriodNumber } from "services/transactionPeriodService";
import { toast } from "react-toastify";
import { LINQ_STATUS } from "CONSTANT/statusCodes";
import ChitPosting from "pages/ChitPosting/ChitPosting";
import DocumentViewer from "components/common/DocumentViewer";
import {
    GridColumnsFilterHeader,
    GridColumnsFilterPopup,
    mergeColumnsWithSavedSettings,
    columnsToSettingValue,
} from "components/common/GridColumnsFilterHeader";
import {
    updateCurrentUserSettings,
    useCurrentUserSettings,
} from "services/userSettingsService";

const USER_SETTING_BATCH_POSTING_COLUMNS_KEY = "BatchPostingGridColumns";

const BATCH_POSTING_TOGGLEABLE_COLUMNS = [
    { title: "Matter", field: "matterID.matterRef", show: true },
    { title: "Ref", field: "reference", show: true },
    { title: "Detail", field: "details", show: true },
    { title: "FE", field: "feeEarnerRef.userRef", show: true },
    { title: "Nominal", field: "nominalRef.nominalRef", show: true },
    { title: "Vat", field: "vatAmount", show: true },
    { title: "Net", field: "netAmount", show: true },
    { title: "Supplier", field: "supplierRef.supplierRef", show: true },
    { title: "Payee(r)", field: "payee", show: true },
    { title: "Documents", field: "documentFiles", show: true },
];

const BatchPostings = ({ type, selectedList, onClose }) => {
    const editField = "inEdit";
    const dispatch = useDispatch();
    const { data: company } = useCompany();
    const [updateDataIndex, setUpdateDataIndex] = useState(0);
    const [vatCode, setVatCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialData, setInitialData] = useState({
        date: new Date(),
    });

    const [currentPostingType, setCurrentPostingType] = useState(null);
    const [showChitModal, setShowChitModal] = useState(false);

    const [list, setList] = useState([]);
    const [renderList, setRenderList] = useState([]);
    const [matterId, setMatterId] = useState(null);
    const [matterIdTo, setMatterIdTo] = useState(null);

    const [isUpdate, setIsUpdate] = useState(false);
    const [postingCode, setPostingCode] = useState();

    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [authErrorMsg, setAuthErrorMsg] = useState("");
    const [failedPostings, setFailedPostings] = useState([]);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [authCode, setAuthCode] = useState("");
    const [showDoc, setShowDoc] = useState(false);
    const [documentViewerData, setDocumentViewerData] = useState([]);
    const { data: userSettings } = useCurrentUserSettings();
    const [columns, setColumns] = useState(BATCH_POSTING_TOGGLEABLE_COLUMNS);
    const [columnsFilterPopupOpen, setColumnsFilterPopupOpen] = useState(false);
    const [columnsFilterAnchorEl, setColumnsFilterAnchorEl] = useState(null);
    const columnsRef = React.useRef(BATCH_POSTING_TOGGLEABLE_COLUMNS);
    const columnsPersistedValueRef = React.useRef(
        columnsToSettingValue(BATCH_POSTING_TOGGLEABLE_COLUMNS),
    );
    const columnsPersistOnCloseRef = React.useRef(false);
    const prevColumnsFilterPopupOpenRef = React.useRef(false);

    const handleAuthCodeChange = (code) => {
        setAuthCode(code || "");
    };

    useEffect(() => {
        if (type === "chitPosting") {
            setList([...selectedList]);
        }
    }, [selectedList]);

    useEffect(() => {
        const saved = userSettings?.find(
            (s) => s.key === USER_SETTING_BATCH_POSTING_COLUMNS_KEY,
        )?.value;
        if (saved !== undefined) {
            const merged = mergeColumnsWithSavedSettings(
                BATCH_POSTING_TOGGLEABLE_COLUMNS,
                saved,
            );
            setColumns(merged);
            columnsRef.current = merged;
            columnsPersistedValueRef.current = columnsToSettingValue(merged);
        }
    }, [userSettings]);

    const handleColumnsChange = (newColumns) => {
        columnsRef.current = newColumns;
        setColumns(newColumns);
    };

    const persistColumnSettingsOnClose = React.useCallback(() => {
        if (columnsPersistOnCloseRef.current) return;

        const currentValue = columnsToSettingValue(columnsRef.current);
        if (currentValue === columnsPersistedValueRef.current) return;

        columnsPersistOnCloseRef.current = true;
        columnsPersistedValueRef.current = currentValue;

        updateCurrentUserSettings([
            {
                key: USER_SETTING_BATCH_POSTING_COLUMNS_KEY,
                value: currentValue,
            },
        ])
            .catch((err) => {
                console.error("Failed to save batch posting column settings", err);
            })
            .finally(() => {
                columnsPersistOnCloseRef.current = false;
            });
    }, []);

    useEffect(() => {
        const wasOpen = prevColumnsFilterPopupOpenRef.current;
        prevColumnsFilterPopupOpenRef.current = columnsFilterPopupOpen;

        if (wasOpen && !columnsFilterPopupOpen) {
            persistColumnSettingsOnClose();
        }
    }, [columnsFilterPopupOpen, persistColumnSettingsOnClose]);

    const handleColumnsFilterPopupClose = React.useCallback(() => {
        setColumnsFilterPopupOpen(false);
    }, []);

    const showColumn = (field) =>
        columns.some((col) => col.field === field && col.show);

    const visibleFilterColumnCount = columns.filter((col) => col.show).length;

    // Handle selected chits from modal
    const handleChitSelection = (selectedChits) => {
        setList((prevList) => [...prevList, ...selectedChits]);
    };

    const onChangePosting = (event) => {
        const code = event?.value?.code;
        setCurrentPostingType(code);
        if (code) {
            setRenderList([code]);
        }
    };

    const handleSelectChit = () => {
        setShowChitModal(true);
    };

    const handleRemove = (row) => {
        if (list.length === 0) return;
        const listcopy = [...list];
        listcopy.splice(row, 1);
        setList([...listcopy]);
    };

    const handleSubmit = async (e) => {
        const data = PostingSubmitFilter(list, company);
        if (data.vatAmount === null) {
            data.vatAmount = 0;
        }

        if (data.length === 0) return;
        const formattedData = data.map((postingItem) => {
            postingItem.transactionPeriod = getSelectedSessionPeriodNumber();
            // FINAL SAFETY CHECK: Ensure isCounselFee is never null
            if (
                postingItem.hasOwnProperty("isCounselFee") &&
                (postingItem.isCounselFee === null ||
                    postingItem.isCounselFee === undefined)
            ) {
                console.warn(
                    "⚠️ Found null isCounselFee, converting to false:",
                    postingItem
                );
                postingItem.isCounselFee = false;
            }
            return postingItem;
        });
        setLoading(true);
        try {
            const response = await api.createposting(formattedData);
            toast.success(
                `Posted Successfully for ${response?.data?.data[0]?.postingType} - ${response?.data?.data[0]?.transactionNo} `
            );
            setList([]);
            if (onClose) {
                onClose();
            }
        } catch (error) {
            const status = error?.response?.status || error?.status;
            if (status === LINQ_STATUS.PostingOverDrawnException) {
                onPostingOverdrawn(error, formattedData);
            } else if (status !== LINQ_STATUS.LinqUserMessageException) {
            }
        } finally {
            setLoading(false);
        }
    };

    const onPostingOverdrawn = (error, formattedData) => {
        let failed = [];
        let msg = "";
        const backendItems = error?.data.errorData || {};
        failed = backendItems;

        if (!backendItems.length && Array.isArray(formattedData)) {
            failed = formattedData.map((p, i) => ({
                postingType: p?.postingType,
                matterRef: p?.matterRef,
                matterID: p?.matterID,
                message: backendItems?.message || "",
                __derived: true,
                __index: i,
            }));
        }
        setAuthErrorMsg(msg);
        setFailedPostings(failed);
        setPendingPayload(formattedData);
        setAuthCode("");
        setShowAuthDialog(true);
    };

    const handleAuthorise = async () => {
        if (!pendingPayload) return;
        setLoading(true);
        try {
            const response = await api.createposting(pendingPayload, {
                allowOverdrawn: true,
                authcode: authCode,
            });
            setShowAuthDialog(false);
            toast.success(
                `Posted Successfully for ${response?.data?.data[0]?.postingType} - ${response?.data?.data[0]?.transactionNo} `
            );
            setList([]);
            if (onClose) {
                onClose();
            }
        } catch (error) {
            const status = error?.response?.status || error?.status;
            if (status === LINQ_STATUS.InvalidUserAuthCode) {
                const respData = error?.response?.data || {};
                setAuthErrorMsg(respData?.message || "Invalid authorisation code.");
                setShowAuthDialog(true);
            } else if (status === LINQ_STATUS.PostingOverDrawnException) {
                onPostingOverdrawn(error, pendingPayload);
            } else {
                setShowAuthDialog(false);
                if (status !== LINQ_STATUS.LinqUserMessageException) {
                }
            }
        } finally {
            setLoading(false);
        }
    };


    const handleViewDocuments = async (dataItem) => {
        setLoading(true);
        try {
            const documents = await fetchBatchPostingDocuments(dataItem);
            if (documents) {
                setDocumentViewerData(documents);
                setShowDoc(true);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const onChangematter = (event) => {
        if (event.value) {
            setMatterId(event.value);
            dispatch(getMattersBalances(event.value.matterId, "All Postings"));
        } else {
            setMatterId(null);
            dispatch(clearMattersBalances("All Postings"));
        }
    };

    const handleUpdate = (props) => {
        const { dataIndex, dataItem } = props;
        const postingType = dataItem.postingType;
        const nextItem = {
            ...dataItem,
            postingType: postingType ? { ...postingType } : postingType,
        };

        onChangematter({ value: nextItem.matterID });
        setMatterIdTo(nextItem?.matterIDTo);
        setUpdateDataIndex(dataIndex);
        setPostingCode(postingType ?? null);
        setRenderList(postingType?.code ? [postingType.code] : []);
        setVatCode(nextItem.vatCode);
        setIsUpdate(true);
        setInitialData(nextItem);
    };

    const setColumnsFilterAnchorElement = React.useCallback((el) => {
        if (!el) return;
        setColumnsFilterAnchorEl((prev) => (el === prev ? prev : el));
    }, []);

    const toggleColumnsFilterPopup = React.useCallback(() => {
        setColumnsFilterPopupOpen((prev) => !prev);
    }, []);

    const ColumnsFilterHeaderCell = React.useCallback(
        (props) => (
            <GridColumnsFilterHeader
                thProps={props}
                setAnchorElement={setColumnsFilterAnchorElement}
                onTogglePopup={toggleColumnsFilterPopup}
            />
        ),
        [setColumnsFilterAnchorElement, toggleColumnsFilterPopup],
    );

    const CommandCell = createBatchPostingCommandCell({
        onEdit: handleUpdate,
        onRemove: handleRemove,
    });
    const DocumentCounterCell = createBatchPostingDocumentCounterCell({
        onViewDocuments: handleViewDocuments,
    });

    return (
        <div className="h-100 pb-3">
            <div className="d-flex h-100 ps-0 ms-0 g-0">
                <Form
                    postingType={type}
                    list={list}
                    setList={setList}
                    setIsUpdate={setIsUpdate}
                    onChangePosting={onChangePosting}
                    updateDataIndex={updateDataIndex}
                    setInitialData={setInitialData}
                    matterId={matterId}
                    matterIdTo={matterIdTo}
                    setMatterIdTo={setMatterIdTo}
                    vatCode={vatCode}
                    setVatCode={setVatCode}
                    isUpdate={isUpdate}
                    setRenderList={setRenderList}
                    renderList={renderList}
                    onChangematter={onChangematter}
                    initialData={initialData}
                    setUpdateDataIndex={setUpdateDataIndex}
                    changePostingCode={setPostingCode}
                    postingCodeEdit={postingCode}
                    openChits={handleSelectChit}
                    type={type}
                />

                <div className="flex-grow-1 overflow-auto h-100 d-flex flex-column">
                    <TransactionPeriod />

                    <div
                        className="mx-3 mt-3 h-100 d-flex flex-column"
                        style={{ minHeight: "110px" }}
                    >
                        {renderList[0] !== "PIN" && (
                            <div className="row mx-0 mt-2 mb-2 gap-1">
                                <div className="col g-0 mb-lg-2 mb-xl-0 p-1">
                                    <MatterDetails matter={matterId} width={"100%"} />
                                </div>
                                <div
                                    className="col-12 col-xxl-6 g-0 mb-lg-2 mb-xl-0 p-1"
                                    style={{ overflowX: "auto" }}
                                >
                                    <MatterAccount
                                        mt="0px"
                                        float="right"
                                        component={"All Postings"}
                                        matterId={matterId}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="mt-3 w-100 d-flex flex-column flex-grow-1 overflow-auto">
                            <Grid
                                data={list}
                                editField={editField}
                                className={`flex-grow-1 overflow-auto fs-md  ${visibleFilterColumnCount > 2 ? "grid-minwidth-col5-150" : "grid-minwidth-col3-150"}`}
                            >
                                <Column
                                    title="Type"
                                    field="postingType.code"
                                    width="65px"
                                    className="px-2"
                                    headerClassName="ps-2"
                                />

                                <Column
                                    title="Date"
                                    field="date"
                                    width="100px"
                                    className="px-2"
                                    cell={DateFormatCell}
                                />

                                {showColumn("matterID.matterRef") && (
                                    <Column
                                        title="Matter"
                                        field="matterID.matterRef"
                                        width="120px"
                                        className="px-2"
                                    />
                                )}

                                {showColumn("reference") && (
                                    <Column title="Ref" field="reference" width="100px" className="px-2" />
                                )}

                                {showColumn("details") && (
                                    <Column title="Detail" field="details" className="px-2" />
                                )}

                                {showColumn("feeEarnerRef.userRef") && (
                                    <Column title="FE" field="feeEarnerRef.userRef" width="60px" />
                                )}

                                {showColumn("nominalRef.nominalRef") && (
                                    <Column title="Nominal" field="nominalRef.nominalRef" className="px-2" />
                                )}

                                {showColumn("vatAmount") && (
                                    <Column
                                        title="Vat"
                                        field="vatAmount"
                                        cell={AmountFormatCell}
                                        className="px-2"
                                        width="80px"
                                        headerClassName="grid-header-center"
                                    />
                                )}

                                {showColumn("netAmount") && (
                                    <Column
                                        title="Net"
                                        field="netAmount"
                                        cell={AmountFormatCell}
                                        className="px-2"
                                        width="100px"
                                        headerClassName="grid-header-center"
                                    />
                                )}

                                {showColumn("supplierRef.supplierRef") && (
                                    <Column title="Supplier" field="supplierRef.supplierRef" />
                                )}

                                {showColumn("payee") && (
                                    <Column title="Payee(r)" cell={PayCell} width="100px" />
                                )}
                                {showColumn("documentFiles") && (
                                    <Column
                                        title=" "
                                        field="documentFiles"
                                        cell={DocumentCounterCell}
                                        width="50px"
                                    />
                                )}
                                <Column
                                    cell={CommandCell}
                                    width="60px"
                                    headerCell={ColumnsFilterHeaderCell}
                                />
                            </Grid>

                            <GridColumnsFilterPopup
                                anchor={columnsFilterAnchorEl}
                                show={columnsFilterPopupOpen}
                                onClose={handleColumnsFilterPopupClose}
                                columns={columns}
                                onColumnsChange={handleColumnsChange}
                            />

                            <div className="buttonstyle justify-content-end align-items-end flex-column flex-xl-row">
                                <div
                                    className="d-flex align-items-stretch rounded-2"
                                    style={{
                                        border: "1px solid #E6E6E6",
                                        overflowX: "auto",
                                        flexShrink: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {(() => {
                                        const vatTotal = list.reduce((sum, i) => sum + (parseFloat(i.vatAmount) || 0), 0);
                                        const netTotal = list.reduce((sum, i) => sum + (parseFloat(i.netAmount) || 0), 0);
                                        return [
                                            { label: "Total Items", value: list.length },
                                            { label: "VAT Total", value: AmountFormat(vatTotal) },
                                            { label: "Net Total", value: AmountFormat(netTotal) },
                                            { label: "Batch Total", value: AmountFormat(vatTotal + netTotal) },
                                        ];
                                    })().map(({ label, value }, idx, arr) => (
                                        <React.Fragment key={label}>
                                            <div
                                                className="d-flex flex-column align-items-end text-muted"
                                                style={{ padding: "2px 14px", minWidth: "80px", flexShrink: 0, fontSize: "0.8rem" }}
                                            >
                                                {label}
                                                <span className="fw-semibold">{value}</span>
                                            </div>
                                            {idx < arr.length - 1 && (
                                                <div style={{ width: "1px", background: "#E6E6E6", margin: "2px 0", flexShrink: 0 }} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <Button
                                    className="common-btn"
                                    disabled={loading || list.length === 0}
                                    onClick={handleSubmit}
                                >
                                    Post
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showAuthDialog && (
                <PostingAuthDialog
                    failedPostings={failedPostings}
                    pendingPayload={pendingPayload}
                    authCode={authCode}
                    authErrorMsg={authErrorMsg}
                    onAuthCodeChange={handleAuthCodeChange}
                    onClose={() => setShowAuthDialog(false)}
                    onAuthorise={handleAuthorise}
                />
            )}

            {/* Select Chit Modal */}
            {showChitModal && (
                <ChitPosting
                    isModal={true}
                    onClose={() => setShowChitModal(false)}
                    onAddToBatch={handleChitSelection}
                    component="batchPosting"
                    postingGroup={type || "chits"}
                    defaultPostingType={
                        currentPostingType ? { code: currentPostingType } : null
                    }
                    defaultMatter={matterId}
                    excludedChitIds={list
                        .map((item) => item.chitId)
                        .filter(Boolean)}
                />
            )}

            {showDoc && (
                <DocumentViewer
                    data={documentViewerData}
                    onClose={() => setShowDoc(false)}
                    component="batchPosting"
                />
            )}
        </div>
    );
};

export default BatchPostings;
